/*
|--------------------------------------------------------------------------
| Postal Code Lookup Helpers
|--------------------------------------------------------------------------
*/

import { useCallback, useRef } from 'react';
import type { UseFormSetValue, FieldValues } from 'react-hook-form';
import type { PostalCodeAutoPopulate } from '../types';

type WhoAmIResponse = {
  country: string;
  region: string;
  city: string;
};

type PostalCodeLookupResult = {
  region?: string;
  country?: string;
  city?: string;
};

const POSTAL_CODE_MIN_LENGTH = 3;
const DEBOUNCE_DELAY_MS = 500;
const WHOAMI_API_BASE = 'https://buynow.lucidmotors.com/api/v1';

const formatLocaleForApi = (locale: string): string => {
  if (locale.length === 2) {
    return `${locale}_us`;
  }
  return locale.replace('-', '_');
};

const extractPostalCode = (value: unknown): string => {
  if (value && typeof value === 'object' && 'target' in value) {
    return (value as React.ChangeEvent<HTMLInputElement>).target.value || '';
  }
  return String(value || '');
};

const lookupPostalCode = async (
  postalCode: string,
  locale: string
): Promise<PostalCodeLookupResult> => {
  const formattedLocale = formatLocaleForApi(locale);
  const response = await fetch(
    `${WHOAMI_API_BASE}/${formattedLocale}/user/whoami?postCode=${postalCode}`
  );

  if (!response.ok) {
    throw new Error(`Postal code lookup failed: ${response.status}`);
  }

  const data: WhoAmIResponse = await response.json();
  return { region: data.region, country: data.country, city: data.city };
};

type UsePostalCodeAutoPopulateParams = {
  config?: PostalCodeAutoPopulate;
  setValue?: UseFormSetValue<FieldValues>;
  locale: string;
  originalOnChange?: (value: unknown) => void;
};

/**
 * Hook to handle postal code auto-population with debouncing
 * @returns onChange handler that wraps the original and triggers postal code lookup
 */
export const usePostalCodeAutoPopulate = ({
  config,
  setValue,
  locale,
  originalOnChange,
}: UsePostalCodeAutoPopulateParams) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLookupRef = useRef<{ postalCode: string; locale: string } | null>(null);

  const handleChange = useCallback(
    (value: unknown) => {
      originalOnChange?.(value);

      if (!config || !setValue) return;

      const postalCode = extractPostalCode(value);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (postalCode.length >= POSTAL_CODE_MIN_LENGTH) {
        timeoutRef.current = setTimeout(async () => {
          if (
            lastLookupRef.current?.postalCode === postalCode &&
            lastLookupRef.current?.locale === locale
          ) {
            return;
          }

          try {
            const result = await lookupPostalCode(postalCode, locale);
            lastLookupRef.current = { postalCode, locale };

            if (result.region && config.regionField) {
              setValue(config.regionField, result.region, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            if (result.country && config.countryField) {
              setValue(config.countryField, result.country, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            if (result.city && config.cityField) {
              setValue(config.cityField, result.city, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          } catch {
            // Silently fail - user can still manually enter values
          }
        }, DEBOUNCE_DELAY_MS);
      }
    },
    [config, setValue, locale, originalOnChange]
  );

  return config ? handleChange : originalOnChange;
};
